import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import SafetyWarningDialog from "../components/SafetyWarningDialog";
import { api } from "../lib/api";
import { PUBLIC_BASE } from "../lib/publicBase";
import { Copy, Sparkles, Store, Crown, Moon, ArrowRight, Gift, Hourglass, Plus, Check, Star, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

/**
 * Welcome — first-time landing for newly-registered Seekers (CLIENT role).
 * Replaces the cold "drop them in the Feed" pattern with a guided start.
 *
 * Shown immediately after OTP verification. Auto-transitions to the feed
 * after 60 seconds, but most users will click one of the 3 quick-start
 * cards before that.
 */
export default function Welcome() {
  const nav = useNavigate();
  const [refCode, setRefCode] = useState("");
  const [referrer, setReferrer] = useState(null);
  const [trendingElders, setTrendingElders] = useState([]);
  const [connecting, setConnecting] = useState({}); // {uid: "idle"|"sending"|"sent"}

  useEffect(() => {
    api.get("/referrals/me").then(({ data }) => setRefCode(data?.code || "")).catch(() => {});
    // If the user signed up via someone else's referral link, spotlight
    // the referring practitioner at the top of the page.
    api.get("/me/referrer").then(({ data }) => setReferrer(data || null)).catch(() => setReferrer(null));
    // First 3 Elders sorted by elderIndex — the most established practitioners
    api
      .get("/providers/elders", { params: { limit: 3 } })
      .then(({ data }) => setTrendingElders((data?.elders || data || []).slice(0, 3)))
      .catch(() => setTrendingElders([]));
  }, []);

  // No auto-redirect — user controls when to leave the welcome page.

  const connect = async (uid) => {
    setConnecting((s) => ({ ...s, [uid]: "sending" }));
    try {
      await api.post("/friends/request", { toUserId: uid });
      setConnecting((s) => ({ ...s, [uid]: "sent" }));
      toast.success("Connection request sent");
    } catch (e) {
      // Already friends / already requested → treat as a success state
      const msg = e.response?.data?.detail || "";
      if (/already|exists|sent/i.test(msg)) {
        setConnecting((s) => ({ ...s, [uid]: "sent" }));
        toast.success("Connection already in progress");
      } else {
        setConnecting((s) => ({ ...s, [uid]: "idle" }));
        toast.error(msg || "Couldn't send request — try from their profile");
      }
    }
  };

  const shareLink = refCode ? `${PUBLIC_BASE}/auth?mode=register&ref=${refCode}` : "";

  const copyLink = () => {
    if (!shareLink) return;
    navigator.clipboard.writeText(shareLink);
    toast.success("Referral link copied to clipboard");
  };

  return (
    <Layout>
      <SafetyWarningDialog trigger="signup" />
      <div className="max-w-4xl mx-auto space-y-10" data-testid="welcome-page">
        {/* Hero */}
        <div className="text-center space-y-4">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-secondary/40 bg-secondary/[0.08]"
               style={{ color: "#D4B58C" }}
               data-testid="welcome-chip">
            <Sparkles size={12} strokeWidth={1.75} />
            <span className="label-mystic">You're in</span>
          </div>
          <h1 className="font-serif text-5xl sm:text-6xl" data-testid="welcome-heading">
            Welcome to Cauldron
          </h1>
          <p className="text-foreground/70 max-w-xl mx-auto text-base leading-relaxed">
            Your account is open and your wallet is ready. Here are three
            doors you can walk through right now.
          </p>
        </div>

        {/* Referring practitioner spotlight — only shown when the new
            seeker arrived via someone's referral link. Acts as a warm
            handoff: "this person brought you here; meet them first." */}
        {referrer && <ReferrerSpotlight referrer={referrer} />}

        {/* Three quick-start cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="welcome-quickstart">
          <QuickStartCard
            to="/marketplace"
            icon={<Store size={22} strokeWidth={1.5} />}
            kicker="Step 1 · The Bazaar"
            title="Browse the Marketplace"
            body="Sacred readings, healings, and rituals from verified practitioners. Filter by tradition or vibe."
            testid="welcome-cta-marketplace"
          />
          <QuickStartCard
            to="/elders"
            icon={<Crown size={22} strokeWidth={1.5} />}
            kicker="Step 2 · The Lineage"
            title="Meet the Elders"
            body="The first 500 practitioners on Cauldron — Founders Wall. Follow the ones whose work resonates."
            testid="welcome-cta-elders"
          />
          <QuickStartCard
            to="/oracle"
            icon={<Moon size={22} strokeWidth={1.5} />}
            kicker="Step 3 · The Question"
            title="Ask the Oracle"
            body="Free, anonymous, instant. A spiritual AI tuned by our Elders — try a 3-card pull or a single-question read."
            testid="welcome-cta-oracle"
          />
        </div>

        {/* Patience note — Cauldron is curated, not flooded */}
        <div
          className="welcome-note relative overflow-hidden parchment p-6 flex items-start gap-4"
          data-testid="welcome-patience-note"
        >
          <Hourglass size={22} className="mt-0.5 shrink-0 relative z-10" style={{ color: "#D4B58C" }} strokeWidth={1.75} />
          <div className="text-base leading-relaxed text-[#F4EEDA] relative z-10">
            <div className="font-serif text-lg mb-2" style={{ color: "#D4B58C" }}>
              A note from the Cauldron
            </div>
            We onboard practitioners slowly and deliberately — every Elder you
            see has been ID-verified, lineage-checked, and approved by hand.
            If the marketplace feels small today, that's by design. Please be
            patient as new trusted and authentic practitioners enter the
            Cauldron. <span style={{ color: "#D4B58C" }} className="font-medium">Quality over scale, always.</span>
          </div>
        </div>

        {/* Trending Elders to follow — pre-populates their Feed */}
        {trendingElders.length > 0 && (
          <div data-testid="welcome-elders-block">
            <div className="flex items-end justify-between mb-4">
              <div>
                <div className="flex items-center gap-2 text-secondary">
                  <Crown size={14} strokeWidth={1.75} />
                  <span className="label-mystic">Trending Elders</span>
                </div>
                <h2 className="font-serif text-2xl mt-1">Start following 3 to fill your feed</h2>
              </div>
              <Link
                to="/elders"
                className="text-xs uppercase tracking-[0.18em] text-foreground/55 hover:text-secondary"
                data-testid="welcome-view-all-elders"
              >
                See all Elders →
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {trendingElders.map((e) => {
                const uid = e.userId || e.id;
                const state = connecting[uid] || "idle";
                const avatar = e.avatarUrl || e.userAvatarUrl;
                return (
                  <div
                    key={uid}
                    className="parchment p-4 flex items-center gap-3"
                    data-testid={`welcome-elder-row-${uid}`}
                  >
                    {avatar ? (
                      <img
                        src={avatar}
                        alt={e.name || e.businessName}
                        className="w-12 h-12 rounded-full object-cover border border-secondary/30"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-secondary/20 flex items-center justify-center font-serif text-lg text-secondary">
                        {(e.name || e.businessName || "?")[0]}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <Link
                        to={`/elders/${uid}`}
                        className="font-serif text-base text-foreground hover:text-secondary truncate block"
                        data-testid={`welcome-elder-name-${uid}`}
                      >
                        {e.name || e.businessName}
                      </Link>
                      {e.tradition && (
                        <div className="text-[11px] text-foreground/55 truncate">{e.tradition}</div>
                      )}
                    </div>
                    {state === "sent" ? (
                      <span
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-secondary/40 text-[10px] uppercase tracking-[0.18em] text-secondary"
                        data-testid={`welcome-elder-following-${uid}`}
                      >
                        <Check size={11} /> Sent
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => connect(uid)}
                        disabled={state === "sending"}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-sm bg-secondary text-secondary-foreground text-[10px] uppercase tracking-[0.18em] hover:bg-secondary/85 disabled:opacity-50"
                        data-testid={`welcome-elder-connect-${uid}`}
                      >
                        <Plus size={11} /> Connect
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Referral nudge */}
        {refCode && (
          <div className="parchment p-6 grid md:grid-cols-[1fr,auto] gap-4 items-center" data-testid="welcome-referral">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Gift size={14} className="text-secondary" strokeWidth={1.75} />
                <span className="label-mystic text-secondary">Referral Vault Active</span>
              </div>
              <h2 className="font-serif text-2xl mb-2">Earn <span className="text-secondary">500 CAUL</span> for every friend you bring</h2>
              <p className="text-foreground/70 text-sm leading-relaxed">
                Your unique code is <span className="font-mono text-secondary">{refCode}</span>.
                Every signup adds 500 CAUL to your wallet, plus 5% of everything they spend for 90 days.
              </p>
              <div className="mt-3 flex items-center gap-2 text-xs text-foreground/60 font-mono break-all">
                {shareLink}
              </div>
            </div>
            <div className="flex flex-col gap-2 md:items-end">
              <button
                type="button"
                onClick={copyLink}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-sm bg-secondary text-secondary-foreground text-xs uppercase tracking-[0.18em] hover:bg-secondary/85"
                data-testid="welcome-copy-referral"
              >
                <Copy size={12} /> Copy link
              </button>
              <Link
                to="/referrals"
                className="text-xs uppercase tracking-[0.18em] text-foreground/55 hover:text-secondary"
                data-testid="welcome-view-referrals"
              >
                View all referrals →
              </Link>
            </div>
          </div>
        )}

        {/* Skip / continue */}
        <div className="text-center pt-4">
          <button
            type="button"
            onClick={() => nav("/feed", { replace: true })}
            className="inline-flex items-center gap-2 text-secondary hover:text-primary text-sm font-semibold uppercase tracking-[0.18em] underline underline-offset-4 decoration-secondary/60 hover:decoration-primary"
            data-testid="welcome-skip"
          >
            Skip — take me to the Feed <ArrowRight size={13} />
          </button>
        </div>
      </div>
    </Layout>
  );
}

function QuickStartCard({ to, icon, kicker, title, body, testid }) {
  return (
    <Link
      to={to}
      className="parchment p-6 block group transition-colors hover:border-secondary/60"
      data-testid={testid}
    >
      <div className="flex items-center gap-2 mb-3 text-secondary">
        {icon}
        <span className="label-mystic text-[10px]">{kicker}</span>
      </div>
      <h3 className="font-serif text-xl mb-2 group-hover:text-secondary transition-colors">{title}</h3>
      <p className="text-foreground/65 text-sm leading-relaxed mb-3">{body}</p>
      <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-secondary opacity-0 group-hover:opacity-100 transition-opacity">
        Open <ArrowRight size={11} />
      </span>
    </Link>
  );
}

/**
 * ReferrerSpotlight
 *
 * Hero card shown at the top of the Welcome page when a new seeker
 * arrived via a practitioner's referral link. Solves the problem of the
 * referral feeling cold — "Why am I even on this app?" — by making the
 * first thing they see the actual person who invited them.
 *
 * Includes a primary CTA (View Profile / Book a Session) so the seeker
 * lands one click away from the practitioner who recommended Cauldron.
 */
function ReferrerSpotlight({ referrer }) {
  const displayName = referrer.businessName || referrer.name || "Your practitioner";
  const subtitle = referrer.isElder
    ? `Founder Elder${referrer.elderIndex ? ` · #${String(referrer.elderIndex).padStart(4, "0")} of 500` : ""}`
    : referrer.isProvider
      ? "Verified Practitioner"
      : "Cauldron Member";
  const initial = (displayName || "?")[0].toUpperCase();

  return (
    <div
      className="relative overflow-hidden rounded-md border border-amber-500/40 bg-gradient-to-br from-amber-950/40 via-purple-950/30 to-amber-950/40"
      data-testid="welcome-referrer-spotlight"
    >
      {referrer.bannerUrl && (
        <div className="absolute inset-0 opacity-25" aria-hidden="true">
          <img
            src={referrer.bannerUrl}
            alt=""
            className="w-full h-full object-cover"
            onError={(e) => { e.target.style.display = "none"; }}
          />
        </div>
      )}
      <div className="relative p-6 sm:p-8">
        <div className="flex items-center gap-2 mb-4">
          <Gift size={13} className="text-amber-300" strokeWidth={1.75} />
          <span className="label-mystic text-amber-300">
            You were invited by
          </span>
        </div>
        <div className="flex flex-col sm:flex-row items-center sm:items-start gap-5">
          <Link
            to={referrer.profileUrl}
            className="shrink-0"
            data-testid="welcome-referrer-avatar-link"
          >
            {referrer.avatarUrl ? (
              <img
                src={referrer.avatarUrl}
                alt={displayName}
                className="w-24 h-24 rounded-full object-cover border-2 border-amber-400/60"
                onError={(e) => { e.target.style.display = "none"; }}
              />
            ) : (
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-amber-500/40 to-purple-700/40 border-2 border-amber-400/60 flex items-center justify-center font-serif text-4xl text-amber-300">
                {initial}
              </div>
            )}
          </Link>
          <div className="flex-1 min-w-0 text-center sm:text-left">
            <Link
              to={referrer.profileUrl}
              className="block"
              data-testid="welcome-referrer-name-link"
            >
              <h2 className="font-serif text-3xl text-foreground hover:text-amber-300 transition-colors">
                {displayName}
              </h2>
            </Link>
            <div className="flex items-center justify-center sm:justify-start gap-2 mt-1 text-amber-300/90 text-sm">
              {referrer.isElder && <Crown size={12} strokeWidth={1.75} />}
              {!referrer.isElder && referrer.verified && <ShieldCheck size={12} strokeWidth={1.75} />}
              <span className="uppercase tracking-[0.18em] text-xs">{subtitle}</span>
            </div>
            {referrer.tradition && (
              <div className="mt-2 text-xs uppercase tracking-[0.15em] text-foreground/60">
                {referrer.tradition}
                {referrer.category ? ` · ${referrer.category}` : ""}
              </div>
            )}
            {referrer.averageRating > 0 && referrer.totalReviews > 0 && (
              <div className="mt-2 inline-flex items-center gap-1 text-sm text-amber-300/90">
                <Star size={12} fill="currentColor" />
                {referrer.averageRating.toFixed(1)} · {referrer.totalReviews} reviews
              </div>
            )}
            {referrer.bio && (
              <p className="mt-3 text-sm text-foreground/75 leading-relaxed line-clamp-3 max-w-xl">
                {referrer.bio}
              </p>
            )}
            <div className="mt-5 flex flex-wrap gap-3 justify-center sm:justify-start">
              <Link
                to={referrer.profileUrl}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded bg-amber-500 text-amber-950 text-xs uppercase tracking-[0.18em] font-medium hover:bg-amber-400"
                data-testid="welcome-referrer-view-profile"
              >
                {referrer.isProvider ? "View profile & book" : "View profile"} <ArrowRight size={12} />
              </Link>
              <Link
                to={`/messages/new?to=${referrer.userId}`}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded border border-amber-500/40 hover:bg-amber-500/10 text-xs uppercase tracking-[0.18em] text-amber-300"
                data-testid="welcome-referrer-message"
              >
                Send a thank-you
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
